import { captureException } from '@sentry/react'
import type { FieldValues, UseFormReturn } from 'react-hook-form'
import { toast } from 'sonner'
import { ApiError } from '@/services/api-client/base'

type Options<T extends FieldValues> = {
  form?: UseFormReturn<T>
}

// Accepts extra positional args so it can be used directly as React Query's
// `onError` callback (which passes `(error, variables, context)`).
export function handleApiError<T extends FieldValues = FieldValues>(
  error: unknown,
  optionsOrVariables?: Options<T> | unknown,
  ..._rest: unknown[]
): void {
  const options: Options<T> =
    optionsOrVariables !== null &&
    typeof optionsOrVariables === 'object' &&
    'form' in (optionsOrVariables as object)
      ? (optionsOrVariables as Options<T>)
      : ({} as Options<T>)
  return _dispatch(error, options)
}

function _dispatch<T extends FieldValues>(
  error: unknown,
  options: Options<T>
): void {
  // 1. Não-ApiError (rede, CORS, runtime). Tratar sem reportar deixa esta
  // classe inteira invisível: em 31/08/2026 a API ficou sem
  // `Access-Control-Allow-Origin` e o app parou para todo mundo sem gerar um
  // único evento — o aviso veio por WhatsApp de uma cliente.
  if (!(error instanceof ApiError)) {
    captureException(error)
    toast.error('Erro inesperado. Tente novamente.')
    if (typeof console !== 'undefined') {
      console.error('[handleApiError] non-ApiError:', error)
    }
    return
  }

  // 2. Token error em fluxo de refresh: BaseApiClient está redirecionando.
  // Só silenciar quando o backend marcou `code: 'token_not_valid'`. 401s sem
  // esse marcador (ex.: credenciais inválidas no login público) devem ser exibidos.
  const code = (error.body as { code?: string } | null)?.code
  if (code === 'token_not_valid') {
    return
  }

  // 5xx é defeito do servidor, não do preenchimento do usuário — vale evento.
  // 4xx fica de fora de propósito: validação e credencial errada são ruído.
  if (error.status >= 500) {
    captureException(error)
  }

  const { form } = options
  const { fieldErrors, nonFieldErrors, message } = error

  const hasForm = form !== undefined
  const hasFieldErrors =
    fieldErrors !== undefined && Object.keys(fieldErrors).length > 0
  const hasNonFieldErrors =
    nonFieldErrors !== undefined && nonFieldErrors.length > 0

  // 3. Form + fieldErrors: inline por campo (primeiro recebe foco).
  if (hasForm && hasFieldErrors) {
    let first = true
    for (const [field, messages] of Object.entries(fieldErrors)) {
      form.setError(
        field as never,
        { type: 'server', message: messages[0] },
        { shouldFocus: first }
      )
      first = false
    }
    if (hasNonFieldErrors) {
      form.setError(
        'root.serverError' as never,
        { type: 'server', message: nonFieldErrors.join(' ') }
      )
    }
    return
  }

  // 4. Form + apenas nonFieldErrors: banner no topo do form.
  if (hasForm && hasNonFieldErrors) {
    form.setError(
      'root.serverError' as never,
      { type: 'server', message: nonFieldErrors.join(' ') }
    )
    return
  }

  // 5. Fallback: toast com message.
  toast.error(message)
}
